import { useCallback, useEffect, useState } from "react";
import TableView from "../components/TableView";
import RetailerTableAction from "../generatedTableActions/RetailerTableAction";
import Button from "../components/Button";
import Badge from "../components/Badge";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import {
  setCurrentPage,
  setPerPage,
  setLoading,
  setRetailer,
  setSearchTerm,
  setTotalPage,
  setTotalCount
} from "../generatedSlice/retailerSlice";
import useSearchApi from "../customHooks/useSearchApi";
import { getRetailer } from "../generatedRequest/retailer";
import { toast } from "react-toastify";
import TableSkeleton from "../components/TableSkeleton";
import RetailerList from "../generatedTableList/RetailerList";
import { TTableProps } from "../types/TableView";
import TableLoader from "../utils/Loaders/tableLoader";
import { useNavigate } from "react-router-dom";

const columns = [
  {
    name: "name",
    renderHeader: (rows: any) => (
      <>
        <b>Retailer Name</b>
      </>
    ),
    renderCell: (row: any) => <>{row?.retailerName}</>,
  },

  {
    name: "url",
    renderHeader: (rows: any) => (
      <>
        <b>Url</b>
      </>
    ),
    renderCell: (row: any) => (
      <>
        {" "}
        <span>
          <a
            href={row?.retailerFrontendUrl}
            target="blank"
            style={{ textDecoration: "underline" }}
          >
            {row?.retailerFrontendUrl}
          </a>
          {"  "}
        </span>
      </>
    ),
  },

  {
    name: "region",
    renderHeader: (rows: any) => (
      <>
        <b>Region</b>
      </>
    ),
    renderCell: (row: any) => (
      <>
        {" "}
        <span>
          <a
            href={"/region/edit?q=" + row?.retailerRegion?._id}
            style={{ textDecoration: "underline" }}
          >
            {row?.retailerRegion?.regionName}
          </a>
          {"  "}
        </span>
      </>
    ),
  },
  {
    name: "status",
    renderHeader: (rows: any) => (
      <>
        <b>Status</b>
      </>
    ),
    renderCell: (row: any) => (
      <Badge
        variant={row?.status == "active" ? "success" : "danger"}
        className="fs-7 fw-bolder"
      >
        {row?.status}
      </Badge>
    ),
  },
  {
    name: "created_by",
    renderHeader: (rows: any) => (
      <>
        <b>Created By</b>
      </>
    ),
    renderCell: (row: any) => <>{row?.createdBy?.overview?.email}</>,
  },

  {
    name: "created_at",
    renderHeader: (rows: any) => (
      <>
        <b>Created At</b>
      </>
    ),
    renderCell: (row: any) => <>{row?.createdAt}</>,
  },
  {
    name: "modified_at",
    renderHeader: (rows: any) => (
      <>
        <b>Modified At</b>
      </>
    ),
    renderCell: (row: any) => <>{row?.updatedAt}</>,
  },
  {
    name: "Actions",
    renderHeader: (rows: any) => (
      <>
        <b>Actions</b>
      </>
    ),
    renderCell: (row: any) => <RetailerTableAction retailer={row} />,
  },
];

const RetailerPage = () => {
  const dispatch = useDispatch();
  const [initload, setInitload] = useState(true);

  const {
    data,
    perPage,
    currentPage,
    totalPage,
    searchTerm,
    loading,
    sortBy,
    sortColumn,
    rerender1,
    totalCount
  } = useSelector((state: RootState) => state.retailer);

  useEffect(() => {
    const fn = async () => {
      const searchQuery = `page=${currentPage}&limit=${perPage}&search=${searchTerm}`;
      dispatch(setLoading(true));
      try {
        const response = await getRetailer(searchQuery);
        if (response.data.status) {
          dispatch(setRetailer(response.data.data.data));
           dispatch(setTotalCount(Math.ceil(response.data.data.totalCount)))
          dispatch(
            setTotalPage(Math.ceil(response.data.data.totalCount / perPage))
          );
        } else {
          toast.error("Something went wrong");
        }
      } catch (error) {
        toast.error("Network Error");
      } finally {
        dispatch(setLoading(false));
        setInitload(false);
      }
    };
    fn();
  }, [rerender1, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(setSearchTerm(''));
    };
  }, [])

  function handleAfterSearch(isSuceess: boolean, data: any, value: any) {
    if (isSuceess) {
      dispatch(setRetailer(data.data.data.data));
      dispatch(setTotalPage(Math.ceil(data.data.data.totalCount / value)));
    }
  }
  const { doSearch, searchLoading } = useSearchApi(
    getRetailer,
    handleAfterSearch
  );

  const handlePageChange = useCallback(
    (value: number) => {
      dispatch(setCurrentPage(value));
      const searchQuery = `page=${value}&limit=${perPage}&search=${searchTerm}`;
      doSearch(searchQuery, perPage);
    },
    [dispatch, perPage, searchTerm, doSearch]
  );

  const handlePerPageChange = useCallback(
    (value: number) => {
      dispatch(setPerPage(value));
      const searchQuery = `page=${currentPage}&limit=${value}&search=${searchTerm}`;
      doSearch(searchQuery, value);
    },
    [dispatch, currentPage, searchTerm, doSearch]
  );

  const handleSearchParams = useCallback(
    (value: string) => {
      dispatch(setCurrentPage(1));
      dispatch(setSearchTerm(value));
      const searchQuery = `page=${"1"}&limit=${perPage}&search=${value}`;
      doSearch(searchQuery, perPage);
    },
    [perPage, dispatch, doSearch]
  );

  const configuration: TTableProps = {
    columns: columns,
    rows: data,
    showPagination: true,
    showSearch: true,
    showPerPage: true,
    paginationData: {
      perPage: perPage,
      currentPage: currentPage,
      totalPage: totalPage,
      totalCount: totalCount
    },
    onPageChange: handlePageChange,
    onPerPageChange: handlePerPageChange,
    handleSearchParams: handleSearchParams,
    searchPlaceholder: "Search Retailers",
    searchLoading: searchLoading,
  };

  const navigate = useNavigate();
  
  if (loading && initload) {
    return <TableSkeleton rows={10} columns={6} />;
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-5">
        <h2>Retailers</h2>
        <Button
          variant="primary"
          size="medium"
          handleButtonClick={() => navigate("add")}
        >
          Add Retailer
        </Button>
      </div>
      <RetailerList {...configuration} />
    </>
  );
};

export default RetailerPage;
